import { routes } from '@/config/routes'
import type { DocumentKind, FlowType } from '@/features/cases'

/**
 * Copy for the three requestable services (ported from the relevant entries of
 * `nivex-design/apps/nivex-web-v3/src/lib/services.ts` — its `path`/`image`/`badge` fields aren't
 * needed here since an *owned* audit/trades flow's "go to it" destination is a real dashboard tab,
 * and an *unrequested* one's "start it" destination goes through `/dashboard/request/:flow`,
 * never the first-time onboarding funnel). `build` has no single document (`docKind` is unused —
 * see `RequestServiceView`'s `flow` prop, which never receives `'build'`). Unlike audit/trades,
 * its `/dashboard/request/build` destination isn't `RequestServiceView` either — it's a dedicated
 * multi-step view (`InvestorBuildPortfolioRequestView`) that collects income/goals/a callback slot
 * and submits via `POST /cases/mine/build-portfolio`, since Build has no single document to upload.
 * The public `/build-portfolio` wizard stays reachable only for logged-out/new-investor signups
 * (`choose-service-view.tsx`), never from here. `tabRoute` points at the real `/dashboard/build`
 * Goals view.
 */
export const SERVICE_META: Record<
  FlowType,
  {
    title: string
    /** Short doc-type label used as `docLabel` by `RequestServiceView`'s upload steps — unused
     * for `build` (its `flow` prop never receives `'build'`, see below). */
    document: string
    action: string
    docKind: DocumentKind
    /** "Upload your X — that's all we need." sentence on the not-yet-requested prompt cards. */
    promptCopy: string
    /** Destination once this flow is owned (`services-list-card.tsx`). */
    tabRoute: string
    /** Destination to start this flow when not yet owned (`NotRequestedCard`/`ServiceUpsell`). */
    startRoute: string
  }
> = {
  audit: {
    title: 'Audit My Portfolio',
    document: 'CAS statement',
    action: 'Start portfolio audit',
    docKind: 'cas',
    promptCopy: "Upload your CAS statement — that's all we need.",
    tabRoute: routes.investor.portfolio,
    startRoute: routes.investor.request('audit'),
  },
  trades: {
    title: 'Audit My Trades',
    document: 'tradebook',
    action: 'Review my trades',
    docKind: 'tradebook',
    promptCopy: "Upload your tradebook — that's all we need.",
    tabRoute: routes.investor.trades,
    startRoute: routes.investor.request('trades'),
  },
  build: {
    title: 'Build My Portfolio',
    document: 'income & goals',
    action: 'Build a plan',
    docKind: 'report',
    promptCopy: "Tell us about your goals — we'll build a plan around them.",
    tabRoute: routes.investor.build,
    startRoute: routes.investor.request('build'),
  },
}
